type StatusDotProps = {
  status: string | null | undefined;
  /** ASA's `displayStatus` — what's actually happening RIGHT NOW
   *  (RUNNING / ON_HOLD / PAUSED / DELETED / etc), as opposed to
   *  `status` which is just the user-set ENABLED/PAUSED toggle.
   *  When provided, takes priority — `status="ENABLED"` paired with
   *  `displayStatus="ON_HOLD"` shows as amber "On hold", not green
   *  "Running" (which would lie about deliverability). */
  displayStatus?: string | null | undefined;
  /** Shows the label next to the dot. Defaults to true. */
  withLabel?: boolean;
};

/**
 * Small coloured dot + optional status label. Replaces StatusBadge in
 * Ads Manager where the denser ASA-style "• Running" affordance is
 * preferred over a pill.
 *
 * Green  = ENABLED / RUNNING — the campaign is actively serving
 * Gray   = PAUSED — user-paused, no spend
 * Red    = any error / ad-hoc bad state
 * Amber  = deliverability issues ASA flagged (e.g. ON_HOLD,
 *          budget exhausted, awaiting review)
 */
export function StatusDot({
  status,
  displayStatus,
  withLabel = true,
}: StatusDotProps) {
  // displayStatus is ASA's authoritative deliverability state and
  // wins whenever it's present. We previously rendered `status`
  // alone, so a campaign that ASA had paused for being on hold
  // (status=ENABLED, displayStatus=ON_HOLD) lit up green "Running"
  // — actively misleading.
  const effective = displayStatus || status;
  const [color, label] = resolve(effective);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12.5,
        color: "var(--c-text-2)",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: color,
          boxShadow: `0 0 0 3px ${colorHalo(color)}`,
        }}
      />
      {withLabel && <span>{label}</span>}
    </span>
  );
}

function resolve(status: string | null | undefined): [string, string] {
  if (!status) return ["var(--c-text-4)", "—"];
  const up = status.toUpperCase();
  if (up === "ENABLED" || up === "RUNNING" || up === "ACTIVE") {
    return ["var(--c-pos-500)", "Running"];
  }
  if (up === "PAUSED") return ["var(--c-text-4)", "Paused"];
  if (up === "DELETED" || up === "REMOVED") {
    return ["var(--c-text-4)", "Deleted"];
  }
  if (up === "ERROR" || up === "FAILED" || up === "INVALID") {
    return ["var(--c-neg-500)", up === "INVALID" ? "Invalid" : "Error"];
  }
  // Apple's deliverability "yellow" states — campaign is technically
  // ENABLED but not serving. Each of these maps to amber so the user
  // immediately sees "this isn't running, but it's not your fault
  // either, fix something upstream".
  if (
    up.startsWith("ON_HOLD") ||
    up === "BUDGET_EXHAUSTED" ||
    up === "OUT_OF_BUDGET" ||
    up === "AWAITING_REVIEW" ||
    up === "PENDING_AD_REVIEW"
  ) {
    if (up === "BUDGET_EXHAUSTED" || up === "OUT_OF_BUDGET") {
      return ["var(--c-warn-500)", "Out of budget"];
    }
    if (up === "AWAITING_REVIEW" || up === "PENDING_AD_REVIEW") {
      return ["var(--c-warn-500)", "In review"];
    }
    return ["var(--c-warn-500)", "On hold"];
  }
  return ["var(--c-text-4)", status];
}

/** Semi-transparent halo colour — lighter version of the dot. */
function colorHalo(dotColor: string): string {
  if (dotColor.includes("pos")) return "var(--c-pos-50)";
  if (dotColor.includes("neg")) return "var(--c-neg-50)";
  if (dotColor.includes("warn")) return "var(--c-warn-50)";
  return "transparent";
}
